import React, { ReactNode, useCallback, useEffect } from 'react';
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useHref,
} from 'react-router-dom';
import {
  ResourceContextProvider,
  usePermissions,
  useRedirect,
} from 'react-admin';
import {
  Box,
  Divider,
  Tab,
  Tabs,
} from '@mui/material';

import actions, { Actions } from '../../constants/actions';
import hasAccess from '../../utilities/hasAccess';
import MemberList from '../member/MemberList';
import MemberCreate from '../member/MemberCreate';
import ApiKeyList from '../apiKeys/ApiKeyList';
import useCurrentTab from '../../hooks/useCurrentTab';
import { useRegisterBreadcrumb } from '../layout/navbar/BreadcrumbsManager';
import MemberEdit from '../member/MemberEdit';
import resourceNames from '../../constants/resourceNames';
import ApiKeyEdit from '../apiKeys/ApiKeyEdit';
import ApiKeyCreate from '../apiKeys/ApiKeyCreate';
import MemberListBreadcrumb from '../member/MemberListBreadcrumb';
import ApiKeyListBreadcrumb from '../apiKeys/ApiKeyListBreadcrumb';
import PermissionRolesListBreadcrumb from '../permissionRoles/PermissionRolesListBreadcrumb';
import PermissionRolesList from '../permissionRoles/PermissionRolesList';
import PermissionRolesView from '../permissionRoles/PermissionRolesView';
import AccountSecuritySettings from '../accountSecurity/AccountSecuritySettings';

const AccessManagementTabsLayout = ({ children }: { children: ReactNode }) => {
  const { permissions } = usePermissions<Actions[]>();
  const redirect = useRedirect();

  const handleTabChange = useCallback((
    _event: React.SyntheticEvent<Element, Event>,
    newValue: string,
  ) => {
    redirect(`../${newValue}`);
  }, [redirect]);

  const activeTab = useCurrentTab();

  return (
    <Box>
      <Tabs value={activeTab === '' ? 'members' : activeTab} onChange={handleTabChange}>
        {hasAccess(permissions, actions.MEMBER_LIST) && (<Tab label="Members" value="members" />)}
        {hasAccess(permissions, actions.API_KEY_LIST) && (<Tab label="API keys" value="api-keys" />)}
        {hasAccess(permissions, actions.PERMISSION_ROLE_LIST) && (<Tab label="Permission settings" value="permission-roles" />)}
        {hasAccess(permissions, actions.ACCOUNT_SECURITY_UPDATE) && (<Tab label="Account security" value="account-security" />)}
      </Tabs>
      <Divider />
      {children}
    </Box>
  );
};

const AccessManagement = () => {
  const basename = useHref('..');
  useRegisterBreadcrumb({
    title: 'Access management',
    path: basename,
  });

  const { permissions } = usePermissions<Actions[]>();

  // Needed to delay the render to avoid incorrect breadcrumb order
  const [render, setRender] = React.useState(false);
  useEffect(() => {
    const timeout = setTimeout(() => setRender(true), 1);
    return () => clearTimeout(timeout);
  }, []);

  let navigateTo;
  if (hasAccess(permissions, actions.MEMBER_LIST)) {
    navigateTo = 'members';
  } else if (hasAccess(permissions, actions.API_KEY_LIST)) {
    navigateTo = 'api-keys';
  } else if (hasAccess(permissions, actions.PERMISSION_ROLE_LIST)) {
    navigateTo = 'permission-roles';
  } else if (hasAccess(permissions, actions.ACCOUNT_SECURITY_UPDATE)) {
    navigateTo = 'account-security';
  } else {
    navigateTo = 'members';
  }
  if (!render) return false;

  return (
    <Routes>
      <Route path="*">
        <Route index element={<Navigate to={navigateTo} replace />} />
        <Route
          path="members"
          element={(
            <ResourceContextProvider value={resourceNames.MEMBERS}>
              <MemberListBreadcrumb />
              <Outlet />
            </ResourceContextProvider>
          )}
        >
          {hasAccess(permissions, actions.MEMBER_LIST) && (
            <Route
              index
              element={(
                <AccessManagementTabsLayout>
                  <Box paddingX={4}>
                    <MemberList />
                  </Box>
                </AccessManagementTabsLayout>
              )}
            />
          )}
          {hasAccess(permissions, actions.MEMBER_CREATE) && (
            <Route path="create" element={<MemberCreate />} />
          )}
          {hasAccess(permissions, actions.MEMBER_GET) && (
            <Route path=":id/*" element={<MemberEdit />} />
          )}
        </Route>
        <Route
          path="api-keys"
          element={(
            <ResourceContextProvider value={resourceNames.API_KEYS}>
              <ApiKeyListBreadcrumb />
              <Outlet />
            </ResourceContextProvider>
          )}
        >
          {hasAccess(permissions, actions.API_KEY_LIST) && (
            <Route
              index
              element={(
                <AccessManagementTabsLayout>
                  <Box paddingX={4}>
                    <ApiKeyList />
                  </Box>
                </AccessManagementTabsLayout>
              )}
            />
          )}
          {hasAccess(permissions, actions.API_KEY_CREATE) && (
            <Route path="create" element={<ApiKeyCreate />} />
          )}
          {hasAccess(permissions, actions.API_KEY_GET) && (
            <Route path=":id/*" element={<ApiKeyEdit />} />
          )}
        </Route>
        {hasAccess(permissions, actions.PERMISSION_ROLE_LIST) && (
          <Route
            path="permission-roles"
            element={(
              <ResourceContextProvider value={resourceNames.PERMISSION_ROLES}>
                <PermissionRolesListBreadcrumb />
                <AccessManagementTabsLayout>
                  <PermissionRolesList />
                </AccessManagementTabsLayout>
              </ResourceContextProvider>
            )}
          >
            {hasAccess(permissions, actions.PERMISSION_ROLE_GET) && (
              <Route
                index
                path=":permissionRoleId"
                element={(
                  <PermissionRolesView />
                )}
              />
            )}
          </Route>
        )}
        {hasAccess(permissions, actions.ACCOUNT_SECURITY_UPDATE) && (
          <Route
            path="account-security"
            element={(
              <AccessManagementTabsLayout>
                <AccountSecuritySettings />
              </AccessManagementTabsLayout>
            )}
          />
        )}
      </Route>
    </Routes>
  );
};

export default AccessManagement;
